import {
    Box,
    Button,
    DialogContent,
    TextField,
    DialogTitle,
    DialogActions,
    FormControl,
    InputLabel,
    MenuItem,
} from '@mui/material';
import Select from '@mui/material/Select';
import colors from 'assets/theme/base/colors';
import pxToRem from 'assets/theme/function/px-to-rem';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch } from 'hooks/redux';
import { Controller, useForm } from 'react-hook-form';
import rgba from 'assets/theme/function/rgba';
import DialogIconClose from 'component/dialog-icon-close';
import {
    useEditPersonageMutation,
    useGetPersonageDataQuery,
} from 'store/slices/cards/personage/edit-personage';
import { CardsPersonageRequest } from 'types/card/card-personage-list';
import { useSphereTitleListQuery } from 'store/slices/cards/sphere/sphere-list';
import SuccessSnackbarEffect from 'component/success-snackbar';

interface Props {
    setOpenEditPersonageDialog: React.Dispatch<React.SetStateAction<boolean>>;
    personageId: string;
    refetch: any;
    personageStatus: string;
}

const EditPersonage: React.FC<Props> = (props: Props) => {
    const {
        setOpenEditPersonageDialog,
        personageId,
        refetch,
        personageStatus,
    } = props;

    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
    } = useForm<CardsPersonageRequest>();

    const { data: sphereList } = useSphereTitleListQuery();

    const { data, isSuccess: dataIsSuccess } = useGetPersonageDataQuery(
        personageId,
        {
            refetchOnMountOrArgChange: true,
        },
    );

    const [editPersonage, { error, isError, isSuccess, isLoading }] =
        useEditPersonageMutation();

    const formRef = useRef<HTMLFormElement>(null);

    const dispatch = useAppDispatch();

    const onSubmit = (values: CardsPersonageRequest) => {
        editPersonage({
            personageTitle: values.personageTitle,
            cardSphereId: personageId,
            albumColor: values.albumColor,
        });
    };

    const closeModal = () => {
        setOpenEditPersonageDialog(false);
        reset();
    };

    useEffect(() => {
        if (error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error?.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [isError]);

    useEffect(() => {
        if (isSuccess) {
            setOpenEditPersonageDialog(false);
            refetch();
        }
    }, [isSuccess]);

    return (
        <>
            <Box
                display={'flex'}
                justifyContent={'center'}
                bgcolor={rgba(colors.primary.main, 0.5)}
            >
                <DialogTitle textTransform={'uppercase'}>
                    EDIT CARD PERSONAGE
                </DialogTitle>
                <DialogIconClose handleClose={closeModal} />
            </Box>

            <DialogContent>
                {dataIsSuccess && (
                    <Box
                        ref={formRef}
                        component="form"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <Box pt={pxToRem(15)} display={'flex'}>
                            <Controller
                                name="personageTitle"
                                control={control}
                                defaultValue={data.title}
                                rules={{ required: 'Title is required' }}
                                render={({
                                    field: { name, value, onChange },
                                }) => (
                                    <TextField
                                        fullWidth
                                        name={name}
                                        value={value ?? data.title}
                                        onChange={onChange}
                                        label="Title"
                                        error={Boolean(errors[name])}
                                        helperText={
                                            errors[name]?.message?.toString() ||
                                            ''
                                        }
                                    />
                                )}
                            />

                            <FormControl sx={{ ml: pxToRem(10) }} fullWidth>
                                <InputLabel
                                    disabled={personageStatus === 'active'}
                                    shrink={true}
                                >
                                    Sphere
                                </InputLabel>
                                <Controller
                                    name="cardSphereId"
                                    control={control}
                                    render={({
                                        field: { name, value, onChange },
                                    }) => (
                                        <Select
                                            disabled={
                                                personageStatus === 'active'
                                            }
                                            label="Sphere"
                                            name={name}
                                            value={value || ''}
                                            onChange={onChange}
                                            displayEmpty
                                            inputProps={{
                                                'aria-label': 'Sphere',
                                            }}
                                        >
                                            {!value && data.sphereTitle && (
                                                <MenuItem value="" disabled>
                                                    {data.sphereTitle}
                                                </MenuItem>
                                            )}
                                            {sphereList?.map((status) => (
                                                <MenuItem
                                                    key={status.id.toString()}
                                                    value={status.id.toString()}
                                                >
                                                    {status.title}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </FormControl>

                            <Controller
                                name="albumColor"
                                control={control}
                                defaultValue={data.albumColor}
                                rules={{
                                    required:
                                        'Personage Album Color is required',
                                }}
                                render={({
                                    field: { name, value = '', onChange },
                                }) => (
                                    <TextField
                                        sx={{ ml: pxToRem(10) }}
                                        fullWidth
                                        name={name}
                                        value={value}
                                        onChange={onChange}
                                        label="Album Color"
                                        error={Boolean(errors[name])}
                                        helperText={
                                            errors[name]?.message?.toString() ||
                                            ''
                                        }
                                    />
                                )}
                            />
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    sx={{ width: '50%' }}
                    onClick={() => formRef.current?.requestSubmit()}
                    variant={'contained'}
                    loading={isLoading}
                >
                    Edit Personage
                </Button>
                <Button
                    sx={{ width: '50%', bgcolor: colors.action.menuSelect }}
                    onClick={() => closeModal()}
                    variant={'contained'}
                >
                    Cancel
                </Button>
            </DialogActions>

            <SuccessSnackbarEffect
                isSuccess={isSuccess}
                message={'Successfully Edit an Personage!'}
            />
        </>
    );
};
export default EditPersonage;
